import { m } from 'framer-motion';
import { useState, useCallback, useEffect } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// hooks
import { INotifications } from 'src/types/daotao';
import { db } from "src/utils/firebaseConfig"; // Đường dẫn tới file cấu hình Firebase
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// _mock
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc } from "firebase/firestore";


// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
//
import NotificationItem from './notification-item';


// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const drawer = useBoolean();

  const smUp = useResponsive('up', 'sm');

  const [currentTab, setCurrentTab] = useState('all');

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const [notifications, setNotifications] = useState<INotifications[]>([]);
  const [totalUnRead, setToalUnRead] = useState(0)
  const [tabs, setTabs] = useState([
    { value: "all", label: "Tất cả", count: 0 },
    { value: "0", label: "Chưa đọc", count: 0 },
    { value: "1", label: "Đã đọc", count: 0 },
  ]);


  useEffect(() => {
    const handleAllNoti = () => {
      const q = query(
        collection(db, "notifications"),
        orderBy("createdAt", "desc")
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const newNotifications: INotifications[] = snapshot.docs.map((document) => {
          const data = document.data();

          return {
            id: document.id,
            avatarUrl: data.avatarUrl || "",
            ID_Lophoc: data.ID_Lophoc || "",
            isUnRead: data.isUnRead ?? 0,
            ID_Connguoi: data.ID_Connguoi || "",
            title: data.title || "Thông báo",
            category: data.category || "Khác",
            type: data.type || "General",
            createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
            updatedAt: data.updatedAt ? data.updatedAt.toDate() : new Date(),
          };
        });
        // ✅ Tính số lượng thông báo theo trạng thái
        const totalUnread = newNotifications.filter((noti) => noti.isUnRead === 0).length;
        const totalRead = newNotifications.filter((noti) => noti.isUnRead === 1).length;
        const totalAll = totalUnread + totalRead;

        // ✅ Cập nhật state tabs
        setTabs([
          { value: "all", label: "Tất cả", count: totalAll },
          { value: "0", label: "Chưa đọc", count: totalUnread },
          { value: "1", label: "Đã đọc", count: totalRead },
        ]);
        setNotifications(newNotifications);
      });

      return () => unsubscribe();
    };

    handleAllNoti();
  }, []);

  useEffect(() => {
    if (notifications) {
      setToalUnRead(notifications.filter((item) => item.isUnRead === 0).length);
    }
  }, [notifications])


  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter((noti) => noti.isUnRead === 0);

      const updatePromises = unreadNotifications.map((noti) =>
        updateDoc(doc(db, "notifications", noti.id), { isUnRead: 1, updatedAt: new Date() })
      );

      await Promise.all(updatePromises);

      setNotifications(
        notifications.map((notification) => ({
          ...notification,
          isUnRead: 1,
        }))
      );
    } catch (error) {
      console.error("❌ Error in handleMarkAllAsRead:", error);
    }
  };

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Thông báo
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Đánh dấu tất cả đã đọc">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {tabs.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === '0' && 'warning') ||
                (tab.value === '1' && 'success') ||
                'default'
              }
            >
              {tab.count}
            </Label>
          }
          sx={{
            '&:not(:last-of-type)': {
              mr: 2,
            },
          }}
        />
      ))}
    </Tabs>
  );


  const filteredNotifications = notifications.filter((notification) => {
    if (currentTab === 'all') return true;
    return String(notification?.isUnRead) === currentTab;
  });

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {filteredNotifications.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} drawer={drawer} setNotifications={setNotifications} />
        ))}
      </List>
    </Scrollbar>
  );


  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1 }}
        >
          {renderTabs}
          <IconButton onClick={handleMarkAllAsRead}>
            <Iconify icon="solar:settings-bold-duotone" />
          </IconButton>
        </Stack>

        <Divider />

        {renderList}

        {/* <Box sx={{ p: 1 }}>
          <Button fullWidth size="large">
            View All
          </Button>
        </Box> */}
      </Drawer>
    </>
  );
}
